import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TNil } from '@app/app.interface';
import { CustomValuesService } from '@core/services/custom-values.service';
import { DeliverableChangeTrackerService } from '@core/services/deliverable-change-tracker.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ExecutionChangeTrackerService } from '@core/services/execution-change-tracker.service';
import { GroupChangeTrackerService } from '@core/services/group-change-tracker.service';
import { ECampaignStatus, EDeliverableType, EOverwriteStatus, EPlatformFeature } from '@enums/campaign.enum';
import { EEventPlatformFeature, EEventType } from '@enums/event-tracking.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  IBriefingNote,
  IGlobalTracking,
  TCampaignApi,
  TCampaignUpdatePayload,
} from '@pages/campaigns/campaigns.interface';
import { BannerCardComponent } from '@shared/components/banner-card/banner-card.component';
import { BaseComponent } from '@shared/components/base.component';
import { BreadcrumbComponent, IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { CampaignDurationUtilitiesService } from '@utilities/campaign-duration-utilities.service';
import { toEventPlatformFeature } from '@utilities/event-payload-mapping.util';
import { snakeCase } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { CreateCampaignDialogComponent } from '../create-campaign-dialog/create-campaign-dialog.component';
import {
  ICreateCampaignDialogData,
  ICreateCampaignDialogResult,
} from '../create-campaign-dialog/create-campaign-dialog.interface';
import { CampaignFormExpansionPanelComponent } from './campaign-form-expansion-panel/campaign-form-expansion-panel.component';
import { CampaignFormPayloadService } from './campaign-form-payload.service';
import {
  ECampaignFormSection,
  ECampaignFormSubmitType,
  ICampaignCreatorForm,
  ICampaignForm,
  ICtaForm,
  ICustomCtaValue,
  IGlobalTrackingForm,
  INavigationState,
} from './campaign-form.interface';
import { CampaignFormStore } from './campaign-form.store';
import { CampaignBasicsComponent } from './form-fields/campaign-basics/campaign-basics.component';
import { haveBriefingNotesChanged } from './form-fields/creators-and-ctas/briefing-note-tracking.util';
import { CreatorsAndCtasComponent } from './form-fields/creators-and-ctas/creators-and-ctas.component';
import { ICreatorsAndCtasValues } from './form-fields/creators-and-ctas/creators-and-ctas.interface';
import {
  ICtaTemplateApi,
  ICtaTemplateApiDeliverables,
  TCtaTemplateCreatePayload,
} from './form-fields/creators-and-ctas/cta-dialog.interface';
import {
  getGroupCategory,
  isGlobalGroupFamily,
  TGroupCategory,
} from './form-fields/creators-and-ctas/group-classification.util';
import { KeywordTrackingComponent } from './form-fields/keyword-tracking/keyword-tracking.component';

@Component({
  imports: [
    BannerCardComponent,
    TranslateModule,
    IconBadgeComponent,
    ReactiveFormsModule,
    MatButtonModule,
    CreatorsAndCtasComponent,
    ContentContainerComponent,
    KeywordTrackingComponent,
    DotsSpinnerComponent,
    IconComponent,
    StatusBadgeComponent,
    TextClampTooltipDirective,
    MatTooltipModule,
    MatExpansionModule,
    CampaignFormExpansionPanelComponent,
    CampaignBasicsComponent,
    NgxSkeletonLoaderModule,
  ],
  providers: [CampaignFormStore],
  selector: 'app-campaign-form',
  templateUrl: './campaign-form.component.html',
})
export class CampaignFormComponent extends BaseComponent implements AfterViewInit {
  public readonly ECampaignFormSection = ECampaignFormSection;
  public readonly ECampaignFormSubmitType = ECampaignFormSubmitType;
  public readonly ECampaignStatus = ECampaignStatus;

  public readonly campaign = signal<TCampaignApi | null>(null);
  public readonly submitType = signal<ECampaignFormSubmitType>(ECampaignFormSubmitType.draft);
  public readonly expandedSectionId = signal<ECampaignFormSection | null>(null);

  public readonly canSaveSection = signal<boolean>(true);
  public readonly sectionChanged = signal<boolean>(false);
  public readonly getValues = signal<boolean>(false);

  public readonly basicsValues = signal<FormGroup<ICampaignForm>['value'] | TNil>(undefined);
  public readonly creatorsAndCtasValues = signal<ICreatorsAndCtasValues | TNil>(undefined);
  public readonly keywordTrackingValues = signal<FormGroup<IGlobalTrackingForm>['value'] | TNil>(undefined);

  public readonly redirectingToCampaignDetails = computed(
    () => this.store.campaignSubmitLoading$() || this.backButtonClicked(),
  );

  public readonly basicsExpanded = computed(() => this.expandedSectionId() === ECampaignFormSection.basics);
  public readonly creatorsAndCtasExpanded = computed(
    () => this.expandedSectionId() === ECampaignFormSection.creatorsAndCtas,
  );
  public readonly keywordTrackingExpanded = computed(
    () => this.expandedSectionId() === ECampaignFormSection.keywordTracking,
  );

  public readonly basicsDisabled = computed(() => this.expandedSectionId() !== null && !this.basicsExpanded());
  public readonly creatorsAndCtasDisabled = computed(
    () => this.expandedSectionId() !== null && !this.creatorsAndCtasExpanded(),
  );
  public readonly keywordTrackingDisabled = computed(
    () => this.expandedSectionId() !== null && !this.keywordTrackingExpanded(),
  );
  public readonly startButtonDisabled = computed(
    () =>
      !this.campaign()?.section1Completed ||
      !this.campaign()?.section2Completed ||
      !this.campaign()?.section3Completed ||
      this.campaign()?.status === ECampaignStatus.ended ||
      this.expandedSectionId() !== null ||
      !this.campaignFullyLoaded(),
  );
  public readonly campaignWithAllLoading = computed(
    () => this.store.campaignWithCustomerLoading$() || this.store.campaignWithAllLoading$(),
  );
  public readonly campaignFullyLoaded = computed(
    () => this.store.campaignWithAllLoaded$() && !this.campaignWithAllLoading(),
  );
  public readonly canSaveDisabledMessage = computed(() => {
    if (this.campaign()?.status === ECampaignStatus.ended) {
      return this.translate.instant('pages.campaignForm.sections.cannotUpdateEndedCampaign');
    }

    if (!this.campaignFullyLoaded()) {
      return this.translate.instant('pages.campaignForm.sections.cannotSaveUntilCampaignIsLoaded');
    }

    if (this.expandedSectionId() === ECampaignFormSection.creatorsAndCtas) {
      return this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.canSaveDisabledMessage');
    }

    return null;
  });

  public breadcrumbs: IBreadcrumb[] = [];
  public navigationState: INavigationState | null = null;

  private readonly backButtonClicked = signal<boolean>(false);

  public readonly store = inject(CampaignFormStore);
  public readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly campaignFormPayloadService = inject(CampaignFormPayloadService);
  private readonly campaignDurationUtilitiesService = inject(CampaignDurationUtilitiesService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly deliverableChangeTracker = inject(DeliverableChangeTrackerService);
  private readonly executionChangeTracker = inject(ExecutionChangeTrackerService);
  private readonly groupChangeTracker = inject(GroupChangeTrackerService);
  private readonly customValuesService = inject(CustomValuesService);

  // Numeric section index for campaign_creation_step_opened's campaign_creation_current_section
  // property (the doc's own example, "e.g. section 2", implies 1-based ordinals, not the enum's
  // string values).
  private static readonly SECTION_NUMBERS: Record<ECampaignFormSection, number> = {
    [ECampaignFormSection.basics]: 1,
    [ECampaignFormSection.creatorsAndCtas]: 2,
    [ECampaignFormSection.keywordTracking]: 3,
  };

  // #6b/#7b/#8b - EDeliverableType has no per-item field at the API level (implied by which typed
  // array it's in - see ICtaTemplateApiDeliverables), so this is needed both to build the baseline
  // (real id -> type) and to look up a CTA's response array for a given form-side deliverable type.
  private static readonly DELIVERABLE_TYPE_TO_ARRAY_FIELD: Record<EDeliverableType, keyof ICtaTemplateApiDeliverables> =
    {
      [EDeliverableType.audioTracking]: 'audioTrackings',
      [EDeliverableType.caption]: 'captions',
      [EDeliverableType.chatBot]: 'chatbots',
      [EDeliverableType.chatbotMulti]: 'chatbotMultis',
      [EDeliverableType.content]: 'contents',
      [EDeliverableType.custom]: 'customs',
      [EDeliverableType.description]: 'descriptions',
      [EDeliverableType.gamePlay]: 'gamePlays',
      [EDeliverableType.hashTag]: 'hashTags',
      [EDeliverableType.mention]: 'mentions',
      [EDeliverableType.message]: 'messages',
      [EDeliverableType.productPlacement]: 'productPlacements',
      [EDeliverableType.showcase]: 'showcases',
      [EDeliverableType.talkingPoint]: 'talkingPoints',
      [EDeliverableType.talkingPointAi]: 'talkingPointAis',
      [EDeliverableType.textTracking]: 'textTrackings',
      [EDeliverableType.title]: 'titles',
      [EDeliverableType.visual]: 'visuals',
    };

  private goBackUrl!: string;
  private currentExpansionPanel: MatExpansionPanel | null = null;
  private getValuesForDurationCalculation = true;
  private getValuesForNameUpdate = false;
  private previousCampaignWithAllLoading = false;
  private shouldOpenBasicsOnLoad = false;
  // #6b/#7b/#8b baseline: real deliverable id -> type (+ per-creator context), as of the last
  // successful load/save. ctaTemplateId is undefined for keyword/global-tracking rows (LF-1);
  // exclusiveTo + deletedCreatorIds feed the per-creator _saved events (LF-4).
  // Established once on initial load (deliverableBaselineEstablished guards against
  // createCampaignEffect() re-running this on every campaign() change) and explicitly
  // re-established inside createSubmitEffect() after diffing a creatorsAndCtas save - never by
  // createCampaignEffect() again after that, so there's no dependency on relative effect ordering
  // between the two.
  private deliverableBaseline = new Map<
    string,
    {
      type: EDeliverableType;
      ctaTemplateId: string | undefined;
      exclusiveTo: string | null;
      deletedCreatorIds: Set<string>;
    }
  >();
  private deliverableBaselineEstablished = false;
  // Wave 9 - execution/kpi counterpart to deliverableBaseline, established/re-established alongside
  // it (see the createCampaignEffect()/diffAndTrackSavedDeliverables() call sites below). executions
  // have no per-item "type" the way deliverables do, so the baseline itself only needs the
  // per-creator context (exclusiveTo + deletedCreatorIds, LF-4); kpiBaseline separately records
  // which kpi types have a non-empty value per execution, since KPI rows always exist structurally
  // (see IKpiFieldChangeTrackingContext's comment) and their added/removed/changed_saved events are
  // decided by that value's presence, not by row existence.
  private executionBaseline = new Map<string, { exclusiveTo: string | null; deletedCreatorIds: Set<string> }>();
  // executionId -> (kpi source -> the kpi row's own id), for kpis with a non-empty value.
  private kpiBaseline = new Map<string, Map<string, string>>();
  // Wave 10 follow-up - executionId -> its owning CTA template's id, so execution/kpi _saved events
  // can report ctaTemplateId (a kpi's ctaTemplateId is always its execution's, no separate map).
  private executionCtaBaseline = new Map<string, string>();
  // ctaTemplateId -> its custom/shared/global classification + assigned creatorIds + platform
  // feature, as of the last successful load/save. platformFeature feeds the round-3 audit
  // requirement that every group_* event carries it.
  private groupBaseline = new Map<
    string,
    { category: TGroupCategory; creatorIds: string[]; platformFeature: EPlatformFeature | null }
  >();
  // Wave 11 - creatorId (null for the global note) -> that note, as of the last successful
  // load/save. Only ever holds notes with real content - the backend's own getBriefingNotes() gate
  // (campaign-form-payload.service.ts) never persists an empty one, so campaign.briefingNotes is
  // already filtered by the time it gets here.
  private briefingNoteBaseline = new Map<string | null, IBriefingNote>();
  // #7, manual QA 2026-08-03 - timeSpent for campaign_creation_updated, measured since the matching
  // campaign_creation_step_opened. Only one section can be open at a time (a refresh discards
  // unsaved state, so nothing needs to survive a reload), so a single slot is enough - no map needed.
  private lastSectionOpenedAt: { section: number; timestamp: number } | null = null;
  private campaignSubmitted = false;

  constructor() {
    super();

    this.campaignDurationUtilitiesService.reset();

    const currentNavigation = this.router.getCurrentNavigation();
    const navigationState = currentNavigation?.extras.state as INavigationState;
    const isOpenBasicsOnLoadRequested = Boolean(navigationState?.openBasicsOnLoad);

    this.navigationState = navigationState;

    if (isOpenBasicsOnLoadRequested) {
      this.clearOpenBasicsOnLoadState();
    }

    this.shouldOpenBasicsOnLoad = Boolean(isOpenBasicsOnLoadRequested && currentNavigation?.trigger === 'imperative');

    // Safe only because no code path in this app navigates directly from one campaign's :id to
    // another's on this same route (same assumption as campaign-details.component.ts - see its
    // comment for why "no custom RouteReuseStrategy" alone wouldn't be enough). Also lets the
    // create-customer/add-platform dialogs opened from this form's tree source campaignId without
    // needing a new dialog-data field.
    this.destroyRef.onDestroy(() => {
      this.eventTrackingService.setCampaignContext(null);
      this.deliverableChangeTracker.reset();
      this.executionChangeTracker.reset();
      this.groupChangeTracker.reset();
      // Round-4 review (2026-08-06) - copiedCtaIds isn't cleared by reset() (see the service's
      // comment); cleared here so a copy marker from a previous form session never leaks into a
      // new one.
      this.groupChangeTracker.resetCopiedCtas();
    });

    this.loadCampaign();
    this.createBreadcrumbEffect();
    this.createSubmitEffect();
    this.createGetValuesEffect();
    this.createCampaignEffect();
  }

  public ngAfterViewInit(): void {
    this.triggerCampaignDurationsCalculation();
  }

  public setCampaign(campaign: TCampaignApi): void {
    this.campaign.set(campaign);
  }

  public createBreadcrumbEffect(): void {
    this.setStaticBreadcrumbs(true);

    effect(() => {
      const campaign = this.campaign();

      if (!campaign) {
        return;
      }

      this.breadcrumbs = [];

      this.setStaticBreadcrumbs();

      if (this.navigationState?.fromCampaignDetails) {
        const link = this.navigationState?.adminMode ? `/admin/campaigns/${campaign.id}` : `/campaigns/${campaign.id}`;
        this.goBackUrl = link;

        this.breadcrumbs.push({
          label: this.translate.instant('pages.campaignForm.banner.campaignDashboard'),
          link,
        });
        this.breadcrumbs.push({
          label: campaign.name || '',
        });

        return;
      }

      this.breadcrumbs.push({
        label: campaign.name || '',
      });
    });
  }

  public onStartCampaignClick(): void {
    if (this.startButtonDisabled() || this.store.campaignSubmitLoading$() || !this.campaignFullyLoaded()) {
      return;
    }

    this.submitType.set(ECampaignFormSubmitType.start);

    requestAnimationFrame(() => {
      this.getSectionValues();
    });
  }

  public goBackToCampaigns(): void {
    this.router.navigate([this.goBackUrl]);
  }

  public goBackToCampaign(): void {
    if (!this.campaign()?.id) {
      return;
    }

    this.backButtonClicked.set(true);

    if (this.navigationState?.adminMode) {
      this.router.navigate(['/admin/campaigns', this.campaign()?.id]);
    } else {
      this.router.navigate(['/campaigns', this.campaign()?.id]);
    }
  }

  public onEditCampaign(): void {
    if (
      this.expandedSectionId() !== null ||
      this.campaign()?.status === ECampaignStatus.ended ||
      !this.campaignFullyLoaded()
    ) {
      return;
    }

    this.getValuesForNameUpdate = true;
    this.getSectionValues();
  }

  public onSectionManageClick(section: ECampaignFormSection): void {
    if (
      this.store.campaignSubmitLoading$() ||
      (section === ECampaignFormSection.basics && this.store.campaignWithCustomerLoading$()) ||
      (section !== ECampaignFormSection.basics && this.store.campaignWithAllLoading$())
    ) {
      return;
    }

    this.sectionChanged.set(false);
    this.updateCanSaveSection(true);
    this.expandedSectionId.set(section);

    const sectionNumber = CampaignFormComponent.SECTION_NUMBERS[section];

    this.lastSectionOpenedAt = { section: sectionNumber, timestamp: Date.now() };

    this.eventTrackingService.track(EEventType.campaignCreationStepOpened, {
      campaignCreationCurrentSection: sectionNumber,
      campaignId: this.campaign()?.id,
    });
  }

  public onSectionCloseClick(expansionPanel: MatExpansionPanel): void {
    if (this.store.campaignSubmitLoading$()) {
      return;
    }

    expansionPanel.close();

    this.campaign.set(structuredClone(this.campaign()));
    this.expandedSectionId.set(null);
    this.sectionChanged.set(false);
  }

  public onSectionSaveClick(expansionPanel: MatExpansionPanel): void {
    if (this.store.campaignSubmitLoading$() || !this.campaignFullyLoaded()) {
      return;
    }

    if (this.campaign()?.status === ECampaignStatus.draft) {
      this.submitType.set(ECampaignFormSubmitType.draft);
    }

    this.currentExpansionPanel = expansionPanel;

    this.getSectionValues();
  }

  public updateCanSaveSection(canSave: boolean): void {
    if (this.campaign()?.status === ECampaignStatus.ended) {
      return this.canSaveSection.set(false);
    }

    this.canSaveSection.set(canSave);
  }

  @HostListener('window:beforeunload', ['$event'])
  public handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.sectionChanged()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private setStaticBreadcrumbs(addLoadingLabel = false): void {
    if (this.navigationState?.adminMode) {
      this.breadcrumbs.push({
        label: this.translate.instant('pages.admin.adminCampaigns.banner.title'),
        link: '/admin/campaigns',
      });
      this.goBackUrl = '/admin/campaigns';
    } else {
      this.breadcrumbs.push({ label: this.translate.instant('pages.campaigns.banner.title'), link: '/campaigns' });
      this.goBackUrl = '/campaigns';
    }

    if (!addLoadingLabel) {
      return;
    }

    if (this.navigationState?.fromCampaignDetails) {
      this.breadcrumbs.push({ label: BreadcrumbComponent.loadingLabel });
    }

    this.breadcrumbs.push({ label: BreadcrumbComponent.loadingLabel });
  }

  private loadCampaign(): void {
    const campaignId = this.route.snapshot.paramMap.get('id') || '';
    const adminMode = this.navigationState?.adminMode ?? false;

    this.store.fetchCampaign({
      adminMode,
      campaignId,
      joins: 'customer,whitelist,team',
    });

    this.store.fetchCampaign({
      adminMode,
      campaignId,
      joins: 'all',
    });
  }

  private processFormSubmission(
    submitType: ECampaignFormSubmitType,
    mappedCtaTemplates: TCtaTemplateCreatePayload[],
  ): void {
    const campaignToSubmit = this.campaignFormPayloadService.getCampaignUpdatePayload(
      this.campaign()!,
      this.basicsValues()!,
      this.creatorsAndCtasValues()!,
      this.keywordTrackingValues()!,
      { mappedCtaTemplates, section: this.expandedSectionId() || ECampaignFormSection.basics, type: submitType },
    );

    // LF-3 (audit 2026-08-06) - run any field-change commits still inside their 800ms debounce
    // window before the request goes out, so a fast save can't lose the immediate event or the
    // dirty mark that the _saved diff reads at response time.
    this.customValuesService.flushPendingTracking();

    this.updateCampaign(campaignToSubmit);
  }

  private updateCampaign(campaignToSubmit: TCampaignUpdatePayload): void {
    const campaignUpdatePayload: TCampaignUpdatePayload = {
      ...campaignToSubmit,
      campaignCreators: campaignToSubmit.campaignCreators.map((campaignCreator) => {
        const campaignCreatorId = this.campaign()?.campaignCreators?.find(
          (c) => c.creatorId === campaignCreator.creatorId,
        )?.id;

        return {
          ...campaignCreator,
          campaignId: this.campaign()?.id || '',
          ...(campaignCreatorId ? { id: campaignCreatorId } : {}),
        };
      }),
      id: this.campaign()?.id || '',
    };

    this.campaignSubmitted = true;
    this.store.updateCampaign(campaignUpdatePayload);
  }

  private createCampaignEffect(): void {
    effect(() => {
      const campaign = this.store.campaign$();
      const campaignWithAllLoading = this.store.campaignWithAllLoading$();
      const didFinishCampaignWithAllLoading = this.previousCampaignWithAllLoading && !campaignWithAllLoading;

      this.previousCampaignWithAllLoading = campaignWithAllLoading;

      if (!campaign) {
        return;
      }

      this.campaign.set(campaign);
      this.eventTrackingService.setCampaignContext(campaign.id, campaign.userGroupId ?? null);

      // Established once, from the fully-loaded campaign only (#5, manual QA 2026-08-03) -
      // loadCampaign() fires two concurrent fetches (customer,whitelist + all), and the faster
      // customer,whitelist response's campaign object has no ctaTemplates at all (that join is
      // noload()'d server-side). Gating on campaignWithAllLoaded$() - already used below for the
      // open-basics-on-load logic - ensures this never runs on that ctaTemplates-less response,
      // which previously froze an empty baseline for the rest of the session (every pre-existing
      // deliverable then looked "new" on the first save). createSubmitEffect() re-establishes the
      // baseline itself after a creatorsAndCtas save (see the field's own comment), so this must
      // not run again on that same campaign() change or it would overwrite the pre-save baseline
      // before createSubmitEffect gets a chance to diff against it.
      if (!this.deliverableBaselineEstablished && this.store.campaignWithAllLoaded$()) {
        this.deliverableBaseline = this.buildDeliverableBaseline(campaign);
        [this.executionBaseline, this.kpiBaseline, this.executionCtaBaseline] =
          this.buildExecutionAndKpiBaseline(campaign);
        this.groupBaseline = this.buildGroupBaseline(campaign);
        this.briefingNoteBaseline = this.buildBriefingNoteBaseline(campaign);
        this.deliverableBaselineEstablished = true;
      }

      if (!this.shouldOpenBasicsOnLoad || !didFinishCampaignWithAllLoading) {
        return;
      }

      setTimeout(() => {
        // EC-2 (audit 2026-08-06) - the load-time auto-open used to set the signal directly,
        // firing no step_opened and recording no timestamp, so the first save of this section
        // reported timeSpent: undefined. Not going through onSectionManageClick() because its
        // loading guards could early-return and leave the section closed; only the tracking +
        // timestamp parts are replicated here.
        this.expandedSectionId.set(ECampaignFormSection.basics);
        this.lastSectionOpenedAt = {
          section: CampaignFormComponent.SECTION_NUMBERS[ECampaignFormSection.basics],
          timestamp: Date.now(),
        };

        this.eventTrackingService.track(EEventType.campaignCreationStepOpened, {
          campaignCreationCurrentSection: CampaignFormComponent.SECTION_NUMBERS[ECampaignFormSection.basics],
          campaignId: campaign.id,
        });
      });

      this.shouldOpenBasicsOnLoad = false;
    });
  }

  private createSubmitEffect(): void {
    effect(() => {
      this.getValues.set(false);

      if (this.store.error$()) {
        // A submit failure and an unrelated fetch's error (which also gets cleared to null
        // later, see campaign-form.store.ts fetchCreators/fetchCustomers) look identical on
        // this shared signal. Clearing the flag here as soon as any error is observed prevents
        // a later, unrelated error$-to-null transition from being misread as this submit
        // having succeeded.
        this.campaignSubmitted = false;

        return;
      }

      if (this.store.campaignSubmitLoading$() || !this.store.campaign$()?.id || !this.campaignSubmitted) {
        return;
      }

      this.campaignSubmitted = false;

      if (this.submitType() === ECampaignFormSubmitType.draft) {
        this.handleDraftSaveSuccess();

        return;
      }

      const startedCampaign = this.store.campaign$()!;

      // EC-4 (audit 2026-08-06) - fires on every successful "Start/Save Campaign" submit, not only
      // on a draft->planned transition (the old gate meant a planned/running campaign's start-submit
      // reported nothing at all). Reverses the Wave-5a decision.
      this.eventTrackingService.track(EEventType.campaignCreationFinished, {
        campaignCreationCreatorCounter: startedCampaign.campaignCreators?.length,
        campaignCreationDeliverableGroupCounter: startedCampaign.ctaTemplates?.length,
        campaignId: startedCampaign.id,
      });

      if (this.navigationState?.adminMode) {
        this.router.navigate(['/admin/campaigns', this.store.campaign$()?.id]);
      } else {
        this.router.navigate(['/campaigns', this.store.campaign$()?.id]);
      }
    });
  }

  private handleDraftSaveSuccess(): void {
    const justSavedSection = this.expandedSectionId();
    const updatedCampaign = this.store.campaign$()!;
    const sectionNumber = justSavedSection ? CampaignFormComponent.SECTION_NUMBERS[justSavedSection] : undefined;

    // CTR-1 (audit 2026-08-06) - campaign-wide totals: creator count, and the count of ALL cta
    // templates (custom/global/shared), per the product decision on general events.
    this.eventTrackingService.track(EEventType.campaignCreationUpdated, {
      campaignCreationCreatorCounter: updatedCampaign.campaignCreators?.length,
      campaignCreationCurrentSection: sectionNumber,
      campaignCreationDeliverableGroupCounter: updatedCampaign.ctaTemplates?.length,
      campaignId: updatedCampaign.id,
      timeSpent: this.getTimeSpentInSection(sectionNumber),
    });

    // #6b/#7b/#8b - must run before resetState() clears creatorsAndCtasValues() below, and before
    // this.deliverableBaseline gets replaced for the next save. See the field's own comment and
    // docs/event-tracking-implementation-map.md's "Deliverable _saved event design".
    // LF-1 (audit 2026-08-06) - the keywordTracking section is included: its rows live in
    // campaign.globalTracking, whose _saved events are decided by this same diff. The
    // CTA-scoped passes no-op on a section-3 save (the submitted CTA values are unchanged since
    // the last baseline).
    if (
      justSavedSection === ECampaignFormSection.creatorsAndCtas ||
      justSavedSection === ECampaignFormSection.keywordTracking
    ) {
      // Round-3 audit (item 6) - the group diff reads the PRE-save deliverableBaseline for removed
      // groups' deliverable counts (getBaselineDeliverableCountForCta), so it must run before
      // diffAndTrackSavedDeliverables rebuilds that baseline from the response (otherwise the
      // count always reads 0 for a fully-removed group).
      this.diffAndTrackSavedGroups(updatedCampaign);
      this.diffAndTrackSavedDeliverables(updatedCampaign);
      this.diffAndTrackSavedExecutionsAndKpis(updatedCampaign);
      this.diffAndTrackSavedBriefingNotes(updatedCampaign);

      // Round-4 review (2026-08-06) - the LF-5 copy guard (isCtaCopied) must stay live for the
      // whole diff sequence above (groupChangeTracker.reset() inside diffAndTrackSavedGroups no
      // longer clears it), so the copied-ids are only wiped once every added pass has read them.
      this.groupChangeTracker.resetCopiedCtas();
    }

    this.campaign.set(updatedCampaign);
    this.resetState();

    setTimeout(() => {
      if (justSavedSection === ECampaignFormSection.basics && !updatedCampaign.section2Completed) {
        this.onSectionManageClick(ECampaignFormSection.creatorsAndCtas);
      } else if (justSavedSection === ECampaignFormSection.creatorsAndCtas && !updatedCampaign.section3Completed) {
        this.onSectionManageClick(ECampaignFormSection.keywordTracking);
      }
    });
  }

  // #7, manual QA 2026-08-03 - undefined (not 0) when there's no matching step_opened for this
  // exact section (e.g. sectionNumber undefined, or a refresh discarded the in-memory timestamp),
  // so campaign_creation_updated doesn't report a bogus 0ms/very-large time under those edge cases.
  private getTimeSpentInSection(sectionNumber: number | undefined): number | undefined {
    if (sectionNumber === undefined || this.lastSectionOpenedAt?.section !== sectionNumber) {
      return undefined;
    }

    return Date.now() - this.lastSectionOpenedAt.timestamp;
  }

  private buildDeliverableBaseline(campaign: TCampaignApi): Map<
    string,
    {
      type: EDeliverableType;
      ctaTemplateId: string | undefined;
      exclusiveTo: string | null;
      deletedCreatorIds: Set<string>;
    }
  > {
    const baseline = new Map<
      string,
      {
        type: EDeliverableType;
        ctaTemplateId: string | undefined;
        exclusiveTo: string | null;
        deletedCreatorIds: Set<string>;
      }
    >();

    campaign.ctaTemplates?.forEach((cta) => {
      (
        Object.entries(CampaignFormComponent.DELIVERABLE_TYPE_TO_ARRAY_FIELD) as [
          EDeliverableType,
          keyof ICtaTemplateApiDeliverables,
        ][]
      ).forEach(([type, field]) => {
        (
          cta[field] as
            | { id: string; exclusiveTo?: string | null; customCtaValues?: ICustomCtaValue[] | null }[]
            | null
            | undefined
        )?.forEach((deliverable) => {
          baseline.set(deliverable.id, {
            ctaTemplateId: cta.id,
            deletedCreatorIds: this.getDeletedCreatorIds(deliverable.customCtaValues),
            exclusiveTo: deliverable.exclusiveTo ?? null,
            type,
          });
        });
      });
    });

    // LF-1 (audit 2026-08-06) - keyword/global-tracking rows live in campaign.globalTracking, not
    // ctaTemplates. Without them in the baseline, the section-3 rows' client ids never reconcile
    // and no _saved event ever fires for them. ctaTemplateId is undefined for these - that's the
    // discriminator (they're not CTA children).
    const { globalTracking } = campaign;

    if (globalTracking) {
      (globalTracking.audioTrackings ?? []).forEach((deliverable) => {
        baseline.set(deliverable.id, {
          ctaTemplateId: undefined,
          deletedCreatorIds: this.getDeletedCreatorIds(deliverable.customCtaValues),
          exclusiveTo: null,
          type: EDeliverableType.audioTracking,
        });
      });

      (globalTracking.textTrackings ?? []).forEach((deliverable) => {
        baseline.set(deliverable.id, {
          ctaTemplateId: undefined,
          deletedCreatorIds: this.getDeletedCreatorIds(deliverable.customCtaValues),
          exclusiveTo: null,
          type: EDeliverableType.textTracking,
        });
      });
    }

    return baseline;
  }

  // LF-4 - the per-creator "deleted for this creator" marker (customCtaValues entry with
  // valueName null + status deleted). Shared by the deliverable/execution baseline builds and the
  // _saved diff passes.
  private getDeletedCreatorIds(
    customCtaValues:
      | Array<{ creatorId?: string | null; status?: EOverwriteStatus; valueName?: string | null }>
      | null
      | undefined,
  ): Set<string> {
    return new Set(
      (customCtaValues ?? [])
        .filter((value) => value.valueName === null && value.status === EOverwriteStatus.deleted && !!value.creatorId)
        .map((value) => value.creatorId!),
    );
  }

  // #6b/#7b/#8b - compares this.deliverableBaseline (pre-save) against what was just submitted
  // (this.creatorsAndCtasValues(), still populated - resetState() hasn't run yet) and the response
  // (updatedCampaign, with real ids). Re-establishes the baseline from updatedCampaign afterward,
  // fully self-contained here so there's no dependency on createCampaignEffect()'s relative
  // ordering (see docs/event-tracking-implementation-map.md's "Deliverable _saved event design").
  private diffAndTrackSavedDeliverables(updatedCampaign: TCampaignApi): void {
    const submittedCtas = this.creatorsAndCtasValues()?.ctasValue ?? [];
    const responseCtas = updatedCampaign.ctaTemplates ?? [];

    this.trackRemovedAndChangedDeliverables(submittedCtas);
    this.trackAddedDeliverables(submittedCtas, responseCtas);
    this.trackAddedKeywordTrackingDeliverables(updatedCampaign);

    this.deliverableChangeTracker.reset();
    this.deliverableBaseline = this.buildDeliverableBaseline(updatedCampaign);
  }

  private trackRemovedAndChangedDeliverables(submittedCtas: FormGroup<ICtaForm>['value'][]): void {
    const submittedCtaIds = new Set(submittedCtas.map((cta) => cta.id).filter((id): id is string => !!id));
    const submittedIds = new Set(
      submittedCtas.flatMap((cta) =>
        (cta.deliverables ?? []).map((deliverable) => deliverable.id).filter((id): id is string => !!id),
      ),
    );

    // LF-1 - keyword-tracking rows are submitted through the keywordTrackingValues signal, not
    // creatorsAndCtasValues; include them in the present-set so removed/changed is detected for
    // them.
    (this.keywordTrackingValues()?.trackings ?? [])
      .map((tracking) => tracking.id)
      .filter((id): id is string => !!id)
      .forEach((id) => submittedIds.add(id));

    this.deliverableBaseline.forEach(
      ({ type: deliverableType, ctaTemplateId, exclusiveTo, deletedCreatorIds }, deliverableId) => {
        if (!submittedIds.has(deliverableId)) {
          // LF-5 (audit 2026-08-06) - a fully-removed CTA's children die with it; the group's own
          // removed_saved covers them, so skip the per-child events. Keyword-tracking rows
          // (ctaTemplateId undefined) are never CTA children and are not skipped.
          if (ctaTemplateId !== undefined && !submittedCtaIds.has(ctaTemplateId)) {
            return;
          }

          this.eventTrackingService.track(EEventType.campaignCreationDeliverableRemovedSaved, {
            // Round-3 audit - keyword-tracking rows (ctaTemplateId undefined) carry no step.
            campaignCreationCurrentStep: ctaTemplateId === undefined ? undefined : 2,
            creatorId: exclusiveTo ?? this.getDefaultCreatorId(ctaTemplateId),
            ctaTemplateId,
            deliverableId,
            deliverableType,
          });

          return;
        }

        // LF-4 (audit 2026-08-06) - per-creator deleted-marker diff: a marker present now but not
        // in the baseline is a per-creator removal (the row still exists for other creators), and
        // the reverse is a restore. Both report the marker's creatorId. The row itself stays in
        // the submitted form (the backend resolves the marker server-side), so this is the only
        // place these removals/restores surface.
        const submittedDeliverable = submittedCtas
          .flatMap((cta) => cta.deliverables ?? [])
          .find((deliverable) => deliverable.id === deliverableId);

        this.trackPerCreatorDeliverableMarkerChanges(deliverableId, deliverableType, {
          baselineDeletedCreatorIds: deletedCreatorIds,
          ctaTemplateId,
          submittedCustomCtaValues: submittedDeliverable?.customCtaValues,
          submittedExclusiveTo: submittedDeliverable?.exclusiveTo,
        });
      },
    );
  }

  // LF-4 - the deliverable half of the per-creator deleted-marker diff (see the caller's comment
  // for the semantics). Extracted so trackRemovedAndChangedDeliverables stays under the project's
  // max-lines-per-function limit.
  private trackPerCreatorDeliverableMarkerChanges(
    deliverableId: string,
    deliverableType: EDeliverableType,
    options: {
      baselineDeletedCreatorIds: Set<string>;
      ctaTemplateId: string | undefined;
      submittedCustomCtaValues:
        | Array<{
            creatorId?: string | null;
            status?: EOverwriteStatus;
            valueName?: string | null;
          }>
        | null
        | undefined;
      submittedExclusiveTo: string | null | undefined;
    },
  ): void {
    const { baselineDeletedCreatorIds, ctaTemplateId, submittedExclusiveTo } = options;
    // Round-3 audit - keyword-tracking rows (ctaTemplateId undefined) carry no step.
    const step = ctaTemplateId === undefined ? undefined : 2;
    const submittedDeletedCreatorIds = this.getDeletedCreatorIds(options.submittedCustomCtaValues);

    submittedDeletedCreatorIds.forEach((creatorId) => {
      if (!baselineDeletedCreatorIds.has(creatorId)) {
        this.eventTrackingService.track(EEventType.campaignCreationDeliverableRemovedSaved, {
          campaignCreationCurrentStep: step,
          creatorId,
          ctaTemplateId,
          deliverableId,
          deliverableType,
        });
      }
    });

    baselineDeletedCreatorIds.forEach((creatorId) => {
      if (!submittedDeletedCreatorIds.has(creatorId)) {
        this.eventTrackingService.track(EEventType.campaignCreationDeliverableAddedSaved, {
          campaignCreationCurrentStep: step,
          creatorId,
          ctaTemplateId,
          deliverableId,
          deliverableType,
        });
      }
    });

    // A row touched by any deleted-marker is reported through the marker events above; the
    // field-edit signal is only meaningful while the row is fully active.
    if (
      this.deliverableChangeTracker.isDirty(deliverableId) &&
      submittedDeletedCreatorIds.size === 0 &&
      baselineDeletedCreatorIds.size === 0
    ) {
      this.eventTrackingService.track(EEventType.campaignCreationDeliverableChangedSaved, {
        campaignCreationCurrentStep: step,
        creatorId: submittedExclusiveTo ?? this.getDefaultCreatorId(ctaTemplateId),
        ctaTemplateId,
        deliverableId,
        deliverableType,
      });
    }
  }

  // Round-6 (2026-08-07) - the single-owner creatorId fallback for _saved events, shared by every
  // added/removed/changed pass. Resolves only when the submitted CTA is not global (isAllCreators)
  // and has exactly one owning campaign creator (a custom CTA). Shared CTAs have no single owner to
  // report, so they get no creatorId from this path - an item's own exclusiveTo (per-creator
  // context) always wins at the call sites.
  private getDefaultCreatorId(ctaId: string | undefined): string | undefined {
    if (!ctaId) {
      return undefined;
    }

    const ctasValue = this.creatorsAndCtasValues()?.ctasValue ?? [];
    const submittedCta = ctasValue.find((cta) => cta.id === ctaId);

    if (!submittedCta || submittedCta.isAllCreators) {
      return undefined;
    }

    const owners =
      this.creatorsAndCtasValues()?.campaignCreatorsValue.filter((campaignCreator) =>
        campaignCreator.ctas?.some((cta) => cta.id === ctaId),
      ) ?? [];

    return owners.length === 1 ? (owners[0].creatorId ?? undefined) : undefined;
  }

  // A submitted CTA whose id isn't a known real id is itself new this save (see #10/#11) - matched
  // to the response's own new CTAs by position, the same tolerance the doc accepts for deliverables
  // (the backend discards client-side ids on create, so there's no id to match on directly).
  // Unlike the nested-deliverable-array case, this ordering guarantee was never independently
  // confirmed against the backend (abstract_crud.py only covers one entity's own array, not
  // cross-entity ordering) - if the two lists don't even have matching lengths, that's a sign
  // something unexpected happened, so skip the positional match entirely rather than risk pairing
  // a new CTA's deliverables with the wrong response CTA (misattributed properties on a real event
  // is worse than a missed one).
  //
  // Shared with trackAddedExecutionsAndKpis (Wave 9) - both need the same "which response CTA does
  // this submitted CTA correspond to" lookup, independent of which nested array they're diffing.
  private matchSubmittedCtasToResponseCtas(
    submittedCtas: FormGroup<ICtaForm>['value'][],
    responseCtas: ICtaTemplateApi[],
  ): Map<FormGroup<ICtaForm>['value'], ICtaTemplateApi> {
    const responseCtaIds = new Set(responseCtas.map((cta) => cta.id));
    const submittedCtaIds = new Set(submittedCtas.map((cta) => cta.id));
    const newSubmittedCtas = submittedCtas.filter((cta) => cta.id && !responseCtaIds.has(cta.id));
    const newResponseCtas = responseCtas.filter((cta) => !submittedCtaIds.has(cta.id));
    const canMatchNewCtasByPosition = newSubmittedCtas.length === newResponseCtas.length;
    const matches = new Map<FormGroup<ICtaForm>['value'], ICtaTemplateApi>();

    submittedCtas.forEach((submittedCta) => {
      const newIndex = newSubmittedCtas.indexOf(submittedCta);
      const responseCta =
        newIndex === -1
          ? responseCtas.find((cta) => cta.id === submittedCta.id)
          : canMatchNewCtasByPosition
            ? newResponseCtas[newIndex]
            : undefined;

      if (responseCta) {
        matches.set(submittedCta, responseCta);
      }
    });

    return matches;
  }

  private trackAddedDeliverables(submittedCtas: FormGroup<ICtaForm>['value'][], responseCtas: ICtaTemplateApi[]): void {
    this.matchSubmittedCtasToResponseCtas(submittedCtas, responseCtas).forEach((responseCta, submittedCta) => {
      this.trackAddedDeliverablesForCta(submittedCta, responseCta);
    });
  }

  private trackAddedDeliverablesForCta(submittedCta: FormGroup<ICtaForm>['value'], responseCta: ICtaTemplateApi): void {
    // LF-5 (audit 2026-08-06) - a copied CTA's children are a copy artifact, not a user add; the
    // copied group's own added_saved covers the copy. The current copy service preserves the source
    // children's ids (so they're never "new" anyway), but this guard keeps it safe if that ever
    // changes. See copyCtaToCreators()'s markCtaCopied().
    if (this.groupChangeTracker.isCtaCopied(submittedCta.id ?? undefined)) {
      return;
    }

    const submittedDeliverablesByType = new Map<EDeliverableType, { exclusiveTo: string | null; id: string }[]>();

    (submittedCta.deliverables ?? []).forEach((deliverable) => {
      if (!deliverable.id || !deliverable.type) {
        return;
      }

      const items = submittedDeliverablesByType.get(deliverable.type) ?? [];

      items.push({ exclusiveTo: deliverable.exclusiveTo ?? null, id: deliverable.id });
      submittedDeliverablesByType.set(deliverable.type, items);
    });

    submittedDeliverablesByType.forEach((submittedItems, deliverableType) => {
      const field = CampaignFormComponent.DELIVERABLE_TYPE_TO_ARRAY_FIELD[deliverableType];
      const responseIds = ((responseCta[field] as { id: string }[] | null | undefined) ?? []).map(
        (deliverable) => deliverable.id,
      );
      const newSubmittedItems = submittedItems.filter((item) => !this.deliverableBaseline.has(item.id));
      const newResponseIds = responseIds.filter((id) => !this.deliverableBaseline.has(id));

      newSubmittedItems.forEach((item, index) => {
        const realId = newResponseIds[index];

        if (!realId) {
          return;
        }

        this.eventTrackingService.track(EEventType.campaignCreationDeliverableAddedSaved, {
          campaignCreationCurrentStep: 2,
          creatorId: item.exclusiveTo ?? this.getDefaultCreatorId(submittedCta.id ?? undefined),
          ctaTemplateId: responseCta.id,
          deliverableId: realId,
          deliverableType,
        });
      });
    });
  }

  // LF-1 (audit 2026-08-06) - the keyword/global-tracking added pass, mirroring
  // trackAddedDeliverablesForCta's per-type positional matching against the response's
  // globalTracking rows. Client-side ids are discarded by the backend on create, so the real ids
  // are matched positionally per type; on a length mismatch nothing fires (same accepted-risk
  // guard as the CTA-level matching). No creatorId/ctaTemplateId - these rows are campaign-global.
  private trackAddedKeywordTrackingDeliverables(updatedCampaign: TCampaignApi): void {
    const responseGlobalTracking = updatedCampaign.globalTracking;

    if (!responseGlobalTracking) {
      return;
    }

    const submittedTrackings = this.keywordTrackingValues()?.trackings ?? [];
    const submittedIdsByType = new Map<EDeliverableType, string[]>();

    submittedTrackings.forEach((tracking) => {
      if (!tracking.id || !tracking.type) {
        return;
      }

      const ids = submittedIdsByType.get(tracking.type) ?? [];

      ids.push(tracking.id);
      submittedIdsByType.set(tracking.type, ids);
    });

    submittedIdsByType.forEach((submittedIds, deliverableType) => {
      const field = CampaignFormComponent.DELIVERABLE_TYPE_TO_ARRAY_FIELD[deliverableType];
      const responseIds = (
        (responseGlobalTracking[field as keyof IGlobalTracking] as { id: string }[] | null | undefined) ?? []
      ).map((deliverable) => deliverable.id);
      const newSubmittedIds = submittedIds.filter((id) => !this.deliverableBaseline.has(id));
      const newResponseIds = responseIds.filter((id) => !this.deliverableBaseline.has(id));

      newSubmittedIds.forEach((_clientId, index) => {
        const realId = newResponseIds[index];

        if (!realId) {
          return;
        }

        // Round-3 audit - keyword-tracking rows are not a flow-dialog step, so no
        // campaignCreationCurrentStep here.
        this.eventTrackingService.track(EEventType.campaignCreationDeliverableAddedSaved, {
          deliverableId: realId,
          deliverableType,
        });
      });
    });
  }

  // Wave 9 - execution/kpi counterpart to buildDeliverableBaseline, keyed by real execution id
  // with per-creator context (exclusiveTo + deletedCreatorIds, LF-4). kpisByExecution records only
  // the non-empty kpi types per execution, since a kpi row's mere existence is never meaningful on
  // its own - see IKpiFieldChangeTrackingContext's comment.
  private buildExecutionAndKpiBaseline(
    campaign: TCampaignApi,
  ): [
    Map<string, { exclusiveTo: string | null; deletedCreatorIds: Set<string> }>,
    Map<string, Map<string, string>>,
    Map<string, string>,
  ] {
    const executionsById = new Map<string, { exclusiveTo: string | null; deletedCreatorIds: Set<string> }>();
    const kpisByExecution = new Map<string, Map<string, string>>();
    const ctaByExecution = new Map<string, string>();

    campaign.ctaTemplates?.forEach((cta) => {
      cta.executions?.forEach((execution) => {
        // LF-4 - per-creator context captured alongside the id, for the marker-diff and
        // exclusiveTo-based creatorId on the _saved events.
        executionsById.set(execution.id, {
          deletedCreatorIds: this.getDeletedCreatorIds(execution.customCtaValues),
          exclusiveTo: execution.exclusiveTo ?? null,
        });
        ctaByExecution.set(execution.id, cta.id);

        // Without this, the first executionType edit of the session on an already-saved execution
        // would look identical to the step's silently-created default row to
        // ExecutionComponent.trackExecutionTypeChange() (both start with hasExecutionBeenAdded()
        // false), spuriously firing campaign_creation_execution_added for an execution that already
        // existed. Re-run every time this baseline is (re)established (initial load and after each
        // save), so newly-added-then-saved executions are correctly marked "already added" too.
        this.executionChangeTracker.markExecutionAdded(execution.id);

        const nonEmptyKpis = this.getNonEmptyKpis(execution.expectedKpis);

        if (nonEmptyKpis.size) {
          kpisByExecution.set(execution.id, nonEmptyKpis);
        }
      });
    });

    return [executionsById, kpisByExecution, ctaByExecution];
  }

  // Wave 9 - execution/kpi counterpart to diffAndTrackSavedDeliverables(). Same ordering
  // requirement (must run before resetState() and before this.executionBaseline/kpiBaseline get
  // replaced) - see that method's own comment.
  private diffAndTrackSavedExecutionsAndKpis(updatedCampaign: TCampaignApi): void {
    const submittedCtas = this.creatorsAndCtasValues()?.ctasValue ?? [];
    const responseCtas = updatedCampaign.ctaTemplates ?? [];

    // LF-6 (audit 2026-08-06) - response KPI rows keyed by (executionId, kpi source) so the
    // added/changed_saved events can report REAL kpi ids. The form's KPI rows can carry
    // client-side uuids (ExpectedKpiListComponent re-mints rows for available KPI types the
    // backend never persisted), so the submitted ids are not trustworthy.
    const responseKpiIdsByExecution = this.buildResponseKpiIdsByExecution(responseCtas);

    this.trackRemovedAndChangedExecutionsAndKpis(submittedCtas, responseKpiIdsByExecution);
    this.trackAddedExecutionsAndKpis(submittedCtas, responseCtas);

    this.executionChangeTracker.reset();
    [this.executionBaseline, this.kpiBaseline, this.executionCtaBaseline] =
      this.buildExecutionAndKpiBaseline(updatedCampaign);
  }

  private buildResponseKpiIdsByExecution(responseCtas: ICtaTemplateApi[]): Map<string, Map<string, string>> {
    const kpiIdsByExecution = new Map<string, Map<string, string>>();

    responseCtas.forEach((cta) => {
      (cta.executions ?? []).forEach((execution) => {
        const kpiIdsBySource = new Map<string, string>();

        (execution.expectedKpis ?? []).forEach((kpi) => {
          if (kpi.id && kpi.kpi) {
            kpiIdsBySource.set(snakeCase(kpi.kpi), kpi.id);
          }
        });

        kpiIdsByExecution.set(execution.id, kpiIdsBySource);
      });
    });

    return kpiIdsByExecution;
  }

  private trackRemovedAndChangedExecutionsAndKpis(
    submittedCtas: FormGroup<ICtaForm>['value'][],
    responseKpiIdsByExecution: Map<string, Map<string, string>>,
  ): void {
    const submittedCtaIds = new Set(submittedCtas.map((cta) => cta.id).filter((id): id is string => !!id));
    const submittedExecutions = submittedCtas.flatMap((cta) => cta.executions ?? []);
    const submittedExecutionIds = new Set(
      submittedExecutions.map((execution) => execution.id).filter((id): id is string => !!id),
    );
    const submittedKpisByExecution = this.buildSubmittedKpisByExecution(submittedExecutions);

    this.executionBaseline.forEach((baselineExecution, executionId) => {
      const baselineKpis = this.kpiBaseline.get(executionId) ?? new Map<string, string>();
      const ctaTemplateId = this.executionCtaBaseline.get(executionId);

      if (!submittedExecutionIds.has(executionId)) {
        // LF-5 (audit 2026-08-06) - a fully-removed CTA's executions die with it; the group's own
        // removed_saved covers them, so skip both the execution and its KPI events.
        if (ctaTemplateId !== undefined && !submittedCtaIds.has(ctaTemplateId)) {
          return;
        }

        this.eventTrackingService.track(EEventType.campaignCreationExecutionRemovedSaved, {
          campaignCreationCurrentStep: 3,
          creatorId: baselineExecution.exclusiveTo ?? this.getDefaultCreatorId(ctaTemplateId),
          ctaTemplateId,
          executionId,
        });

        baselineKpis.forEach((expectedKpiId, source) => {
          this.eventTrackingService.track(EEventType.campaignCreationKpiRemovedSaved, {
            campaignCreationCurrentStep: 4,
            creatorId: baselineExecution.exclusiveTo ?? this.getDefaultCreatorId(ctaTemplateId),
            ctaTemplateId,
            executionId,
            expectedKpiId,
            source,
          });
        });

        return;
      }

      // LF-4 (audit 2026-08-06) - per-creator deleted-marker diff, same shape as the deliverable
      // one in trackRemovedAndChangedDeliverables().
      const submittedExecution = submittedExecutions.find((execution) => execution.id === executionId);

      const hasMarkerChanges = this.trackPerCreatorExecutionMarkerChanges(
        executionId,
        ctaTemplateId,
        baselineExecution.deletedCreatorIds,
        submittedExecution?.customCtaValues,
        submittedExecution?.exclusiveTo,
      );

      // A per-creator marker-deleted execution's KPI rows are hidden with it - the marker events
      // above are the signal, not per-KPI events.
      if (hasMarkerChanges) {
        return;
      }

      const submittedKpis = submittedKpisByExecution.get(executionId) ?? new Map<string, string>();

      // The KPI rows live under the execution, so creatorId mirrors the execution's own resolution
      // (LF-4): the current submission's exclusiveTo wins (this pass only ever runs for executions
      // still present in the form - removed ones go through the loop above, so the baseline's
      // exclusiveTo would be stale after a custom->shared/global flip), and the single-owner CTA
      // fallback applies when the execution has no per-creator context.
      this.trackRemovedAndChangedKpisForExecution(executionId, ctaTemplateId, baselineKpis, submittedKpis, {
        creatorId: submittedExecution?.exclusiveTo ?? this.getDefaultCreatorId(ctaTemplateId),
        responseKpiIdsBySource: responseKpiIdsByExecution.get(executionId),
      });
    });
  }

  private buildSubmittedKpisByExecution(
    submittedExecutions: Array<{
      expectedKpis?: Array<{ expectedValue?: number | null; id?: string | null; kpi?: TExpectedKpi | null }> | null;
      id?: string | null;
    }>,
  ): Map<string, Map<string, string>> {
    const submittedKpisByExecution = new Map<string, Map<string, string>>();

    submittedExecutions.forEach((execution) => {
      if (!execution.id) {
        return;
      }

      submittedKpisByExecution.set(execution.id, this.getNonEmptyKpis(execution.expectedKpis));
    });

    return submittedKpisByExecution;
  }

  // LF-4 - the execution half of the per-creator deleted-marker diff (see the deliverable
  // counterpart's caller comment for the semantics). Returns whether any marker change was seen,
  // so the caller can skip the KPI diff for a marker-deleted execution.
  private trackPerCreatorExecutionMarkerChanges(
    executionId: string,
    ctaTemplateId: string | undefined,
    baselineDeletedCreatorIds: Set<string>,
    submittedCustomCtaValues:
      | Array<{
          creatorId?: string | null;
          status?: EOverwriteStatus;
          valueName?: string | null;
        }>
      | null
      | undefined,
    submittedExclusiveTo: string | null | undefined,
  ): boolean {
    const submittedDeletedCreatorIds = this.getDeletedCreatorIds(submittedCustomCtaValues);

    submittedDeletedCreatorIds.forEach((creatorId) => {
      if (!baselineDeletedCreatorIds.has(creatorId)) {
        this.eventTrackingService.track(EEventType.campaignCreationExecutionRemovedSaved, {
          campaignCreationCurrentStep: 3,
          creatorId,
          ctaTemplateId,
          executionId,
        });
      }
    });

    baselineDeletedCreatorIds.forEach((creatorId) => {
      if (!submittedDeletedCreatorIds.has(creatorId)) {
        this.eventTrackingService.track(EEventType.campaignCreationExecutionAddedSaved, {
          campaignCreationCurrentStep: 3,
          creatorId,
          ctaTemplateId,
          executionId,
        });
      }
    });

    const executionHasMarkerChanges = submittedDeletedCreatorIds.size > 0 || baselineDeletedCreatorIds.size > 0;

    if (this.executionChangeTracker.isDirty(executionId) && !executionHasMarkerChanges) {
      this.eventTrackingService.track(EEventType.campaignCreationExecutionChangedSaved, {
        campaignCreationCurrentStep: 3,
        creatorId: submittedExclusiveTo ?? this.getDefaultCreatorId(ctaTemplateId),
        ctaTemplateId,
        executionId,
      });
    }

    return executionHasMarkerChanges;
  }

  private trackRemovedAndChangedKpisForExecution(
    executionId: string,
    ctaTemplateId: string | undefined,
    baselineKpis: Map<string, string>,
    submittedKpis: Map<string, string>,
    {
      creatorId,
      responseKpiIdsBySource,
    }: {
      creatorId?: string;
      responseKpiIdsBySource?: Map<string, string>;
    },
  ): void {
    baselineKpis.forEach((expectedKpiId, source) => {
      if (!submittedKpis.has(source)) {
        this.eventTrackingService.track(EEventType.campaignCreationKpiRemovedSaved, {
          campaignCreationCurrentStep: 4,
          creatorId,
          ctaTemplateId,
          executionId,
          expectedKpiId,
          source,
        });
      } else if (this.executionChangeTracker.isKpiDirty(executionId, source)) {
        // LF-6 - the submitted row's id may be a client-side uuid (ExpectedKpiListComponent
        // re-mints rows for KPI types the backend never persisted); the response row's real id is
        // authoritative. Skip the event when the response lacks the row (backend anomaly) rather
        // than report a client id.
        const responseKpiId = responseKpiIdsBySource?.get(source);

        if (!responseKpiId) {
          return;
        }

        this.eventTrackingService.track(EEventType.campaignCreationKpiChangedSaved, {
          campaignCreationCurrentStep: 4,
          creatorId,
          ctaTemplateId,
          executionId,
          expectedKpiId: responseKpiId,
          source,
        });
      }
    });

    submittedKpis.forEach((_expectedKpiId, source) => {
      if (!baselineKpis.has(source)) {
        const responseKpiId = responseKpiIdsBySource?.get(source);

        if (!responseKpiId) {
          return;
        }

        this.eventTrackingService.track(EEventType.campaignCreationKpiAddedSaved, {
          campaignCreationCurrentStep: 4,
          creatorId,
          ctaTemplateId,
          executionId,
          expectedKpiId: responseKpiId,
          source,
        });
      }
    });
  }

  private trackAddedExecutionsAndKpis(
    submittedCtas: FormGroup<ICtaForm>['value'][],
    responseCtas: ICtaTemplateApi[],
  ): void {
    this.matchSubmittedCtasToResponseCtas(submittedCtas, responseCtas).forEach((responseCta, submittedCta) => {
      this.trackAddedExecutionsAndKpisForCta(submittedCta, responseCta);
    });
  }

  private trackAddedExecutionsAndKpisForCta(
    submittedCta: FormGroup<ICtaForm>['value'],
    responseCta: ICtaTemplateApi,
  ): void {
    // LF-5 (audit 2026-08-06) - copied CTAs' children are copy artifacts; the copied group's own
    // added_saved covers the copy (see trackAddedDeliverablesForCta's comment).
    if (this.groupChangeTracker.isCtaCopied(submittedCta.id ?? undefined)) {
      return;
    }

    const submittedExecutions = submittedCta.executions ?? [];
    const submittedIds = submittedExecutions.map((execution) => execution.id).filter((id): id is string => !!id);
    const responseIds = (responseCta.executions ?? []).map((execution) => execution.id);
    const newSubmittedIds = submittedIds.filter((id) => !this.executionBaseline.has(id));
    const newResponseIds = responseIds.filter((id) => !this.executionBaseline.has(id));

    newSubmittedIds.forEach((clientId, index) => {
      const realId = newResponseIds[index];

      if (!realId) {
        return;
      }

      const submittedExecution = submittedExecutions.find((execution) => execution.id === clientId);
      // LF-4 - a custom-mode execution carries its own exclusiveTo; prefer it over the CTA-level
      // resolution for per-creator context.
      const creatorId = submittedExecution?.exclusiveTo ?? this.getDefaultCreatorId(submittedCta.id ?? undefined);

      this.eventTrackingService.track(EEventType.campaignCreationExecutionAddedSaved, {
        campaignCreationCurrentStep: 3,
        creatorId,
        ctaTemplateId: responseCta.id,
        executionId: realId,
      });

      // LF-6 (audit 2026-08-06) - reconcile the KPI rows' real ids from the response by KPI type
      // (source = snake_case of the kpi type; rows are unique per execution, so this is
      // deterministic). Previously the client-side expectedKpiId was reported, which never matched
      // anything downstream. When the response lacks a row for a submitted source (backend
      // anomaly) the event is skipped rather than fired with a client id.
      const responseExecution = (responseCta.executions ?? []).find((execution) => execution.id === realId);
      const responseKpiIdsBySource = new Map<string, string>();

      (responseExecution?.expectedKpis ?? []).forEach((kpi) => {
        if (kpi.id && kpi.kpi) {
          responseKpiIdsBySource.set(snakeCase(kpi.kpi), kpi.id);
        }
      });

      this.getNonEmptyKpis(submittedExecution?.expectedKpis).forEach((_clientId, source) => {
        const realKpiId = responseKpiIdsBySource.get(source);

        if (!realKpiId) {
          return;
        }

        this.eventTrackingService.track(EEventType.campaignCreationKpiAddedSaved, {
          campaignCreationCurrentStep: 4,
          creatorId,
          ctaTemplateId: responseCta.id,
          executionId: realId,
          expectedKpiId: realKpiId,
          source,
        });
      });
    });
  }

  // Returns kpi-type source -> the row's own id (Wave 10 - kpi _saved events carry expectedKpiId
  // alongside executionId+source), for every kpi with a genuinely non-empty value. A kpi row
  // always exists once its execution does (see IKpiFieldChangeTrackingContext's comment), so
  // "which kpis are meaningfully present" is about the value, not the row - null/undefined
  // expectedValue means the row is a structural placeholder, not a real kpi goal.
  private getNonEmptyKpis(
    expectedKpis: { id?: string | null; kpi?: TExpectedKpi | null; expectedValue?: number | null }[] | null | undefined,
  ): Map<string, string> {
    const kpisBySource = new Map<string, string>();

    (expectedKpis ?? []).forEach((kpi) => {
      if (!kpi.kpi || !kpi.id || kpi.expectedValue === null || kpi.expectedValue === undefined) {
        return;
      }

      kpisBySource.set(snakeCase(kpi.kpi), kpi.id);
    });

    return kpisBySource;
  }

  // Wave 10 - a "group" is a CTA template, so its own id doubles as ctaTemplateId; no separate
  // matching/reconciliation step is needed the way deliverables/executions need (their ids are
  // nested inside a CTA, not the CTA's own id). creatorIds (not just a count) is kept alongside
  // category so a later removed/changed _saved event can report a shared group's full creatorId
  // list, same rule as the immediate group events (creators-and-ctas.component.ts's
  // getGroupEventCreatorId).
  private buildGroupBaseline(
    campaign: TCampaignApi,
  ): Map<string, { category: TGroupCategory; creatorIds: string[]; platformFeature: EPlatformFeature | null }> {
    const baseline = new Map<
      string,
      { category: TGroupCategory; creatorIds: string[]; platformFeature: EPlatformFeature | null }
    >();

    campaign.ctaTemplates?.forEach((cta) => {
      const creatorIds = (cta.ctaCreators ?? []).map((ctaCreator) => ctaCreator.creatorId);

      baseline.set(cta.id, {
        category: getGroupCategory(cta.isAllCreators, creatorIds.length),
        creatorIds,
        platformFeature: cta.platformFeature ?? null,
      });
    });

    return baseline;
  }

  // Mirrors creators-and-ctas.component.ts's getGroupEventCreatorId (kept as a separate copy since
  // that one is private to a different component) - 'global' never needs a creatorId, 'custom' has
  // exactly one, 'shared' reports every assigned creator's id, comma-separated.
  private getGroupEventCreatorId(category: TGroupCategory, creatorIds: string[]): string | undefined {
    if (category === 'global') {
      return undefined;
    }

    // Sorted so the same creator set always joins into the same string regardless of source order -
    // this baseline is built from the backend's ctaCreators array while the immediate-event copy of
    // this helper builds from live campaignCreatorsValue; without a shared sort key the two could
    // report the same creators in a different order.
    return category === 'shared' ? [...creatorIds].sort().join(',') : creatorIds[0];
  }

  // Wave 10 - group counterpart to diffAndTrackSavedDeliverables(). Same ordering requirement as
  // that method (see its own comment).
  private diffAndTrackSavedGroups(updatedCampaign: TCampaignApi): void {
    const submittedCtas = this.creatorsAndCtasValues()?.ctasValue ?? [];
    const responseCtas = updatedCampaign.ctaTemplates ?? [];

    this.trackRemovedAndChangedGroups(submittedCtas);
    this.trackAddedGroups(submittedCtas, responseCtas);

    this.groupChangeTracker.reset();
    this.groupBaseline = this.buildGroupBaseline(updatedCampaign);
  }

  private trackRemovedAndChangedGroups(submittedCtas: FormGroup<ICtaForm>['value'][]): void {
    const submittedCtaIds = new Set(submittedCtas.map((cta) => cta.id).filter((id): id is string => !!id));
    const campaignCreatorsValue = this.creatorsAndCtasValues()?.campaignCreatorsValue ?? [];

    this.groupBaseline.forEach((previousEntry, ctaId) => {
      const submittedCta = submittedCtaIds.has(ctaId) ? submittedCtas.find((cta) => cta.id === ctaId) : undefined;

      this.trackRemovedOrChangedGroupSaved(ctaId, previousEntry, submittedCta, campaignCreatorsValue);
    });
  }

  // Wave 10/round-3 - one baseline entry's removed/flip/changed handling (see the caller's
  // comment for the ordering requirement behind the pre-save deliverable count).
  private trackRemovedOrChangedGroupSaved(
    ctaId: string,
    previousEntry: { category: TGroupCategory; creatorIds: string[]; platformFeature: EPlatformFeature | null },
    submittedCta: FormGroup<ICtaForm>['value'] | undefined,
    campaignCreatorsValue: FormGroup<ICampaignCreatorForm>['value'][],
  ): void {
    const {
      category: previousCategory,
      creatorIds: previousCreatorIds,
      platformFeature: previousPlatformFeature,
    } = previousEntry;
    const wasGlobalFamily = isGlobalGroupFamily(previousCategory);
    const removedEventType = wasGlobalFamily
      ? EEventType.campaignCreationGlobalGroupRemovedSaved
      : EEventType.campaignCreationCustomGroupRemovedSaved;
    const removedCreatorId = this.getGroupEventCreatorId(previousCategory, previousCreatorIds);
    const previousPlatformFeatureEventValue = previousPlatformFeature
      ? toEventPlatformFeature(previousPlatformFeature)
      : undefined;

    if (!submittedCta) {
      this.trackGroupRemovedSaved(
        ctaId,
        removedEventType,
        removedCreatorId,
        previousPlatformFeatureEventValue,
        previousCreatorIds.length,
      );

      return;
    }

    const newCreatorIds = campaignCreatorsValue
      .filter((creator) => creator.ctas?.some((cta) => cta.id === ctaId))
      .map((creator) => creator.creatorId!);
    const newCategory = getGroupCategory(submittedCta.isAllCreators, newCreatorIds.length);
    const isGlobalFamilyNow = isGlobalGroupFamily(newCategory);
    const newPlatformFeatureEventValue = submittedCta.platformFeature
      ? toEventPlatformFeature(submittedCta.platformFeature)
      : undefined;

    if (isGlobalFamilyNow !== wasGlobalFamily) {
      this.trackGroupRemovedSaved(
        ctaId,
        removedEventType,
        removedCreatorId,
        previousPlatformFeatureEventValue,
        previousCreatorIds.length,
      );
      this.trackGroupAddedSavedEvent(ctaId, newCategory, newCreatorIds, {
        deliverableGroupCounter: submittedCta.deliverables?.length,
        platformFeature: newPlatformFeatureEventValue,
      });

      return;
    }

    if (this.groupChangeTracker.isDirty(ctaId)) {
      this.eventTrackingService.track(
        isGlobalFamilyNow
          ? EEventType.campaignCreationGlobalGroupChangedSaved
          : EEventType.campaignCreationCustomGroupChangedSaved,
        {
          campaignCreationCreatorCounter: newCreatorIds.length,
          campaignCreationDeliverableGroupCounter: submittedCta.deliverables?.length,
          creatorId: this.getGroupEventCreatorId(newCategory, newCreatorIds),
          ctaTemplateId: ctaId,
          platformFeature: newPlatformFeatureEventValue,
        },
      );
    }
  }

  // CTR-1/round-3 - the removed_saved emission shared by the full-removal and family-flip
  // branches: group-scoped creator reach + the group's own deliverables count (read from the
  // PRE-save baseline) + platformFeature.
  private trackGroupRemovedSaved(
    ctaId: string,
    removedEventType: EEventType,
    removedCreatorId: string | undefined,
    platformFeatureEventValue: EEventPlatformFeature | undefined,
    previousCreatorCount: number,
  ): void {
    this.eventTrackingService.track(removedEventType, {
      campaignCreationCreatorCounter: previousCreatorCount,
      campaignCreationDeliverableGroupCounter: this.getBaselineDeliverableCountForCta(ctaId),
      creatorId: removedCreatorId,
      ctaTemplateId: ctaId,
      platformFeature: platformFeatureEventValue,
    });
  }

  // CTR-1/round-3 - the added_saved emission shared by the family-flip branch and the genuinely-
  // new-group pass: group-scoped creator reach + the group's own deliverables count +
  // platformFeature (the _saved counterpart of the immediate trackGroupAddedEvent).
  private trackGroupAddedSavedEvent(
    ctaId: string,
    category: TGroupCategory,
    creatorIds: string[],
    options: { deliverableGroupCounter?: number; platformFeature?: EEventPlatformFeature },
  ): void {
    const isGlobalFamily = isGlobalGroupFamily(category);

    this.eventTrackingService.track(
      isGlobalFamily
        ? EEventType.campaignCreationGlobalGroupAddedSaved
        : EEventType.campaignCreationCustomGroupAddedSaved,
      {
        campaignCreationCreatorCounter: creatorIds.length,
        campaignCreationDeliverableGroupCounter: options.deliverableGroupCounter,
        creatorId: this.getGroupEventCreatorId(category, creatorIds),
        ctaTemplateId: ctaId,
        newValue: isGlobalFamily ? category : undefined,
        platformFeature: options.platformFeature,
      },
    );
  }

  // CTR-1 - how many deliverables the pre-save baseline attributes to a group, reported on its
  // removed_saved events (the submitted form no longer contains the removed CTA).
  private getBaselineDeliverableCountForCta(ctaTemplateId: string): number {
    let count = 0;

    this.deliverableBaseline.forEach(({ ctaTemplateId: owningCtaId }) => {
      if (owningCtaId === ctaTemplateId) {
        count += 1;
      }
    });

    return count;
  }

  private trackAddedGroups(submittedCtas: FormGroup<ICtaForm>['value'][], responseCtas: ICtaTemplateApi[]): void {
    this.matchSubmittedCtasToResponseCtas(submittedCtas, responseCtas).forEach((responseCta, submittedCta) => {
      if (!submittedCta.id || this.groupBaseline.has(submittedCta.id)) {
        return;
      }

      const creatorIds = (responseCta.ctaCreators ?? []).map((ctaCreator) => ctaCreator.creatorId);
      const category = getGroupCategory(responseCta.isAllCreators, creatorIds.length);

      // CTR-1 - group-scoped counts on specific events: the group's creator reach and its own
      // deliverables count. Round-3 audit - platformFeature on every group_* event.
      // ctaTemplateId is the response's real id (a genuinely new group has no baseline id yet).
      this.trackGroupAddedSavedEvent(responseCta.id, category, creatorIds, {
        deliverableGroupCounter: submittedCta.deliverables?.length,
        platformFeature: responseCta.platformFeature ? toEventPlatformFeature(responseCta.platformFeature) : undefined,
      });
    });
  }

  private buildBriefingNoteBaseline(campaign: TCampaignApi): Map<string | null, IBriefingNote> {
    const baseline = new Map<string | null, IBriefingNote>();

    campaign.briefingNotes?.forEach((note) => baseline.set(note.creatorId, note));

    return baseline;
  }

  // Wave 11 - briefing-note counterpart to diffAndTrackSavedGroups(). No live "dirty" tracker needed
  // here (unlike groups) - a briefing note's content only ever changes through
  // upsertBriefingNote(), so a direct content comparison against the baseline is sufficient.
  private diffAndTrackSavedBriefingNotes(updatedCampaign: TCampaignApi): void {
    const responseNotesByCreatorId = new Map(
      (updatedCampaign.briefingNotes ?? []).map((note) => [note.creatorId, note]),
    );

    this.briefingNoteBaseline.forEach((previousNote, creatorId) => {
      const isGlobal = creatorId === null;
      const properties = { campaignId: updatedCampaign.id, creatorId: creatorId ?? undefined };
      const responseNote = responseNotesByCreatorId.get(creatorId);

      if (!responseNote) {
        this.eventTrackingService.track(
          isGlobal
            ? EEventType.campaignCreationGlobalBriefingNotesRemovedSaved
            : EEventType.campaignCreationCustomBriefingNotesRemovedSaved,
          properties,
        );

        return;
      }

      if (haveBriefingNotesChanged(previousNote, responseNote)) {
        this.eventTrackingService.track(
          isGlobal
            ? EEventType.campaignCreationGlobalBriefingNotesChangedSaved
            : EEventType.campaignCreationCustomBriefingNotesChangedSaved,
          properties,
        );
      }
    });

    responseNotesByCreatorId.forEach((_note, creatorId) => {
      if (this.briefingNoteBaseline.has(creatorId)) {
        return;
      }

      const isGlobal = creatorId === null;

      this.eventTrackingService.track(
        isGlobal
          ? EEventType.campaignCreationGlobalBriefingNotesAddedSaved
          : EEventType.campaignCreationCustomBriefingNotesAddedSaved,
        { campaignId: updatedCampaign.id, creatorId: creatorId ?? undefined },
      );
    });

    this.briefingNoteBaseline = this.buildBriefingNoteBaseline(updatedCampaign);
  }

  private createGetValuesEffect(): void {
    effect(() => {
      const basicsValues = this.basicsValues();
      const creatorsAndCtasValues = this.creatorsAndCtasValues();
      const keywordTrackingValues = this.keywordTrackingValues();

      if (creatorsAndCtasValues === undefined) {
        return;
      }

      if (this.getValuesForDurationCalculation) {
        this.campaignDurationUtilitiesService.calculateCampaignDurations(
          this.campaign()!,
          creatorsAndCtasValues!.campaignCreatorsValue,
          creatorsAndCtasValues!.ctasValue,
        );
        this.getValuesForDurationCalculation = false;

        return;
      }

      if (
        this.getValuesForNameUpdate &&
        this.getValues() &&
        basicsValues !== undefined &&
        creatorsAndCtasValues !== undefined &&
        keywordTrackingValues !== undefined
      ) {
        this.openEditCampaignDialog();
        this.getValuesForNameUpdate = false;

        return;
      }

      if (
        this.getValues() &&
        basicsValues !== undefined &&
        creatorsAndCtasValues !== undefined &&
        keywordTrackingValues !== undefined
      ) {
        // getMappedCtaTemplates rebuilds and deep-clones the full CTA/deliverable graph (images
        // included) - computed once here and passed to both calls below instead of letting each one
        // rebuild it separately, which is what they did before.
        const mappedCtaTemplates = this.campaignFormPayloadService.getMappedCtaTemplates(
          this.campaign()!,
          creatorsAndCtasValues!.campaignCreatorsValue,
          creatorsAndCtasValues!.ctasValue,
        );

        this.processFormSubmission(this.submitType() ?? ECampaignFormSubmitType.draft, mappedCtaTemplates);
        this.campaignDurationUtilitiesService.calculateCampaignDurations(
          this.campaign()!,
          creatorsAndCtasValues!.campaignCreatorsValue,
          creatorsAndCtasValues!.ctasValue,
          mappedCtaTemplates,
        );
      }
    });
  }

  private resetState(): void {
    this.currentExpansionPanel?.close();
    this.expandedSectionId.set(null);
    this.updateCanSaveSection(true);
    this.sectionChanged.set(false);
    this.getValues.set(false);
    this.basicsValues.set(undefined);
    this.creatorsAndCtasValues.set(undefined);
    this.keywordTrackingValues.set(undefined);
  }

  private getSectionValues(): void {
    this.basicsValues.set(undefined);
    this.creatorsAndCtasValues.set(undefined);
    this.keywordTrackingValues.set(undefined);
    this.getValues.set(true);

    // afterNextRender (not requestAnimationFrame) - it's guaranteed to run after Angular has
    // flushed the effects that react to getValues() becoming true (campaign-basics,
    // creators-and-ctas, keyword-tracking's own getValues effects, and this component's own
    // createGetValuesEffect), whereas a raw rAF callback isn't ordered relative to Angular's
    // effect scheduling and could run first, resetting getValues back to false before any of
    // those effects ever observed it as true - silently dropping the whole save/duration-calc/
    // name-update attempt with no error.
    afterNextRender(
      () => {
        this.getValues.set(false);
      },
      { injector: this.injector },
    );
  }

  private triggerCampaignDurationsCalculation(): void {
    this.getValuesForDurationCalculation = true;
    this.getSectionValues();
  }

  private clearOpenBasicsOnLoadState(): void {
    const state = history.state as { [key: string]: unknown };

    if (state?.['openBasicsOnLoad']) {
      history.replaceState({ ...state, openBasicsOnLoad: false }, '', window.location.href);
    }
  }

  private openEditCampaignDialog(): void {
    const data: ICreateCampaignDialogData = {
      campaign: this.campaign() ?? null,
      campaignUpdatePayload: this.campaignFormPayloadService.getCampaignUpdatePayload(
        this.campaign()!,
        this.basicsValues()!,
        this.creatorsAndCtasValues()!,
        this.keywordTrackingValues()!,
        { section: ECampaignFormSection.basics, type: ECampaignFormSubmitType.draft },
      ),
    };

    this.dialog
      .open(CreateCampaignDialogComponent, {
        autoFocus: true,
        data,
        minWidth: '700px',
      })
      .afterClosed()
      .subscribe((result: ICreateCampaignDialogResult | undefined) => {
        if (!result) {
          return;
        }

        const campaign = this.campaign();

        this.campaign.set({
          ...campaign!,
          iconId: result.iconId ?? campaign?.iconId ?? null,
          name: result.name ?? campaign?.name ?? '',
        });

        this.resetState();
      });
  }
}
